import { currencyScale, minorToMajor } from '@wit-support/ui-domain';
// ---- деньги -----------------------------------------------------------------------------------
/** «25,5» / «25.50» / «$ 1 200» → минорные единицы; нечитаемое или ≤ 0 — `null`. */
export function parseMoneyInput(text, currency) {
    const cleaned = text.replace(/[\s$€£¥]/g, '').replace(',', '.');
    if (cleaned === '' || !/^\d+(\.\d+)?$/.test(cleaned))
        return null;
    const scale = currencyScale(currency);
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    // не больше знаков, чем у валюты: «25,555» — ошибка, а не молчаливое округление
    const frac = cleaned.split('.')[1] ?? '';
    if (frac.length > scale)
        return null;
    return Math.round(n * 10 ** scale);
}
/** Минорные единицы → текст поля ввода («500», «25,50»). */
export function moneyInputText(minor, currency) {
    const scale = currencyScale(currency);
    const major = minorToMajor(minor, currency);
    if (Number.isInteger(major))
        return String(major);
    return major.toFixed(scale).replace('.', ',');
}
/** Изменение суммы в процентах от текущей (целое, со знаком). */
export function percentChange(from, to) {
    if (!(from > 0))
        return null;
    return Math.round(((to - from) / from) * 100);
}
/** «+25 %» / «−10 %» (минус — «−»). */
export function percentText(p) {
    return `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p)} %`;
}
// ---- бюджет и ставка --------------------------------------------------------------------------
export function budgetBody(type, amountMinor) {
    return { kind: 'budget', budgetType: type, amountMinor };
}
/** Нужна ли сумма ставки при этой стратегии. */
export const strategyNeedsAmount = (s) => s === 'BID_CAP' || s === 'COST_CAP';
/** Тело ставки: только изменённое; `null` — менять нечего. */
export function bidPatch(initial, form) {
    const body = { kind: 'bid' };
    if (form.strategy !== null && form.strategy !== initial.strategy)
        body.bidStrategy = form.strategy;
    if (strategyNeedsAmount(form.strategy) && form.amountMinor !== null) {
        if (form.amountMinor !== initial.amountMinor)
            body.bidAmountMinor = form.amountMinor;
    }
    if (form.strategy === 'ROAS' && form.roasFloor !== null && form.roasFloor !== initial.roasFloor) {
        body.roasFloor = form.roasFloor;
    }
    return body.bidStrategy !== undefined ||
        body.bidAmountMinor !== undefined ||
        body.roasFloor !== undefined
        ? body
        : null;
}
/** Прочиталось ли всё, что форма показывает и пересылает: иначе правку таргетинга закрываем целиком. */
export function targetingReadable(s) {
    const t = s.targeting;
    return t.geo !== null && t.age !== null && t.gender !== null;
}
/** Страны правятся только когда заданы именно страны (не группы стран, регионы, города). */
export const geoEditableOf = (s) => s.targeting.geoEditable && s.targeting.geo !== null && s.targeting.geo.mode !== 'NON_COUNTRY';
export function formFromSettings(s) {
    const t = s.targeting;
    const geo = t.geo !== null && t.geo.mode === 'COUNTRIES'
        ? { mode: 'COUNTRIES', countries: [...t.geo.countries] }
        : { mode: 'WORLDWIDE' };
    return {
        geo,
        excludedCountries: [...t.excludedCountries],
        age: { min: t.age?.min ?? null, max: t.age?.max ?? null },
        gender: t.gender ?? 0,
        interests: t.interests.map((i) => ({ id: i.id, name: i.name })),
        customAudienceIds: t.customAudiences.map((a) => a.id),
        excludedAudienceIds: t.excludedAudiences.map((a) => a.id),
        advantageAudience: t.advantageAudience,
    };
}
const sameSet = (a, b) => a.length === b.length && new Set(a).size === new Set(b).size && a.every((x) => b.includes(x));
const uniq = (a) => [...new Set(a)];
/** Разворачивает выбранные списки стран в коды: страны списка ДОБАВЛЯЮТСЯ к уже выбранным. */
export function addCountriesFromLists(current, lists, pickedIds) {
    const added = lists.filter((l) => pickedIds.includes(l.id)).flatMap((l) => [...l.countries]);
    return uniq([...current, ...added]);
}
export function validateTargetingForm(form, opts = {}) {
    const out = [];
    const geoOn = opts.geoEditable !== false;
    if (opts.advantageKnown === false && opts.advantageChosen !== true) {
        out.push({
            field: 'advantage',
            message: 'В Meta не задано, включена ли Advantage+ аудитория. Выберите «включена» или «выключена».',
        });
    }
    if (geoOn && form.geo.mode === 'COUNTRIES' && form.geo.countries.length === 0) {
        out.push({ field: 'geo', message: 'Выберите хотя бы одну страну или «Весь мир».' });
    }
    const { min, max } = form.age;
    if (min === null || max === null || !Number.isInteger(min) || !Number.isInteger(max)) {
        out.push({ field: 'age', message: 'Укажите возраст «от» и «до» числами.' });
    }
    else if (min < 18 || max > 65) {
        out.push({ field: 'age', message: 'Возраст — от 18 до 65 лет.' });
    }
    else if (min > max) {
        out.push({
            field: 'age',
            message: `«От» не может быть больше «до»: ${min} > ${max}. Исправьте возраст.`,
        });
    }
    else if (form.advantageAudience && max < 65) {
        out.push({ field: 'age', message: 'При Advantage+ верхняя граница возраста — 65.' });
    }
    if (geoOn && form.geo.mode === 'COUNTRIES') {
        const both = form.geo.countries.filter((c) => form.excludedCountries.includes(c));
        if (both.length > 0) {
            out.push({
                field: 'excluded',
                message: `Страна ${both.join(', ')} одновременно в «Страны» и в «Исключить страны».`,
            });
        }
    }
    const dup = form.customAudienceIds.filter((a) => form.excludedAudienceIds.includes(a));
    if (dup.length > 0) {
        out.push({
            field: 'audiences',
            message: 'Одна аудитория не может быть и в «включить», и в «исключить».',
        });
    }
    if (form.interests.length > 25) {
        out.push({ field: 'interests', message: 'Интересов не больше 25.' });
    }
    return out;
}
/** Тело таргетинга: только изменённые поля; `null` — менять нечего. */
export function targetingPatch(initial, form, opts = {}) {
    const body = { kind: 'targeting' };
    const geoOn = opts.geoEditable !== false;
    const interestsOn = opts.interestsEditable !== false;
    const g0 = initial.geo;
    const g1 = form.geo;
    if (!geoOn) {
        // страны и исключения не трогаем: в группе регионы, города или группы стран
    }
    else if (g0.mode !== g1.mode)
        body.geo = g1;
    else if (g1.mode === 'COUNTRIES' &&
        g0.mode === 'COUNTRIES' &&
        !sameSet(g0.countries, g1.countries)) {
        body.geo = { mode: 'COUNTRIES', countries: uniq(g1.countries) };
    }
    if (geoOn && !sameSet(initial.excludedCountries, form.excludedCountries)) {
        body.excludedCountries = uniq(form.excludedCountries);
    }
    if (form.age.min !== null &&
        form.age.max !== null &&
        (form.age.min !== initial.age.min || form.age.max !== initial.age.max)) {
        body.age = { min: form.age.min, max: form.age.max };
    }
    if (form.gender !== initial.gender)
        body.gender = form.gender;
    if (interestsOn &&
        !sameSet(initial.interests.map((i) => i.id), form.interests.map((i) => i.id))) {
        // в Meta и в наш сервер уходят только id; имена сервер возьмёт из справочника
        body.interests = form.interests.map((i) => ({ id: i.id }));
    }
    if (!sameSet(initial.customAudienceIds, form.customAudienceIds)) {
        body.customAudienceIds = uniq(form.customAudienceIds);
    }
    if (!sameSet(initial.excludedAudienceIds, form.excludedAudienceIds)) {
        body.excludedAudienceIds = uniq(form.excludedAudienceIds);
    }
    // Advantage+ не задан в Meta: человек выбрал значение — оно уходит явно, даже если совпало с «по умолчанию»
    if (opts.sendAdvantage === true || form.advantageAudience !== initial.advantageAudience) {
        body.advantageAudience = form.advantageAudience;
    }
    const changed = Object.keys(body).some((k) => k !== 'kind');
    return changed ? body : null;
}
//# sourceMappingURL=patch-builder.js.map